import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation, AfterViewInit } from '@angular/core';

import { fuseAnimations } from '@fuse/animations';
import { Subject, Observable, BehaviorSubject, merge } from 'rxjs';
import { DataSource } from '@angular/cdk/table';
import { takeUntil, tap } from 'rxjs/operators';
import { MatPaginator, MatSort, MatDialog, MatDialogRef, MatSnackBar } from '@angular/material';
import { RecommenderService } from '@fuse/services/recommender/recommender.service';
import { CompanyService } from '@fuse/services/company.service';
import { TranslateService } from '@ngx-translate/core';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { DateAdapter } from '@angular/material/core';

@Component({
    selector: 'recommender-list',
    templateUrl: './recommender-list.component.html',
    styleUrls: ['./recommender-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations
})
export class RecommenderListComponent implements OnInit, AfterViewInit, OnDestroy {
    private _unsubscribeAll: Subject<any>;

    dataSource: FilesDataSource | null;
    displayedColumns = ['name', 'date_of_birth', 'email', 'created_at'];
    recommendations: any;
    totalPages = 2;
    totalElements = 0;
    sortField = 'created_at';
    sordOrder = 'desc';
    pageSize = 10;
    lang = 'sr';
    company: any;

    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;

    constructor(
        private _recommenderService: RecommenderService,
        public _matDialog: MatDialog,
        private _matSnackBar: MatSnackBar,
        private _translate: TranslateService,
        private _translationLoader: FuseTranslationLoaderService,
        private _companyService: CompanyService,
        private dateAdapter: DateAdapter<Date>
    ) {
        this._unsubscribeAll = new Subject();
        this.dateAdapter.setLocale('en-gb');
    }

    private getTranslations(): void {
        this._translationLoader.getLastLang()
            .then(response => {
                this.lang = response['code'];
                this._translationLoader.getTranslations(this.lang, '25');
                this.dateAdapter.setLocale(this.lang);
            })
            .catch(err => Promise.reject(err))
            .then(() => {
            }, err => {
            });
    }

    private updateDataDateFormat(data): void {

        if (data && data.length > 0) {
            console.log(data);
            data.forEach(element => {
                let d = new Date(element.extra_date);
                let datestring = d.getDate() + '/' + (d.getMonth() + 1) + '/' + d.getFullYear() + '  ' +
                    ('0' + d.getHours()).slice(-2) + ':' + ('0' + d.getMinutes()).slice(-2) + ':' + ('0' + d.getSeconds()).slice(-2);
                element.extra_date = element.extra_date ? datestring : '';

                d = new Date(element.created_at);
                datestring = d.getDate() + '/' + (d.getMonth() + 1) + '/' + d.getFullYear() + '  ' +
                    ('0' + d.getHours()).slice(-2) + ':' + ('0' + d.getMinutes()).slice(-2) + ':' + ('0' + d.getSeconds()).slice(-2);
                element.created_at = element.created_at ? datestring : '';
            });
        }

        return data;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    loadRecommenderPage(): void {
        this.dataSource.loadRecommender(this.paginator.pageIndex + 1, this.paginator.pageSize, 
            this.sort.active || 'created_at', this.sort.direction || 'desc', '', this.company.id);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnInit(): void {
        console.log('here');
        this.dataSource = new FilesDataSource(this._recommenderService);
        this._companyService.getActiveCompany()
            .then(response => {
                this.company = response;
                console.log(this.company);
            })
            .catch(err => Promise.reject(err))
            .then(() => {
                this.loadRecommenderPage();
            }, err => {
            });   

        this._recommenderService.onRecommendationsChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response) => {
                this.totalPages = Math.floor(response.total / this.paginator.pageSize) + 1;
                this.totalElements = response.total;
                // if (firstRun) {
                //     firstRun = false;
                // } else {
                this.dataSource.setData(response.data);
                // }
            });

        this._translationLoader.onLangChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(value => {
                this.getTranslations();
            });

        this._companyService.onCompanyChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response) => {
                if (this.company) {
                    if (this.company.id !== this._companyService.activeCompany.id ? this._companyService.activeCompany.id : response.id) {
                        this.company = this._companyService.activeCompany;
                        this.loadRecommenderPage();
                    }
                }
            });

        this.getTranslations();

    }

    ngAfterViewInit(): void {
        // reset the paginator after sorting
        this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);

        // on sort or paginate events, load a new page
        merge(this.sort.sortChange, this.paginator.page)
            .pipe(
                tap(() => this.loadRecommenderPage())
            )
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe();
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }
}

// Custom Angular CDK Data Source
export class FilesDataSource extends DataSource<any>
{
    private recommenderSubject = new BehaviorSubject<any[]>([]);
    private loadingSubject = new BehaviorSubject<boolean>(false);

    public loading$ = this.loadingSubject.asObservable();

    /**
     * Constructor
     *
     * @param {RecommenderService} _recommenderService
     */
    constructor(
        private _recommenderService: RecommenderService
    ) {
        super();
    }

    connect(): Observable<any[]> {
        return this.recommenderSubject.asObservable();
    }

    disconnect(): void {
        this.recommenderSubject.complete();
        this.loadingSubject.complete();
    }

    setData(data): void {
        this.recommenderSubject.next(data);
        this.loadingSubject.next(false);
    }

    loadRecommender(page: number = 1, limit: number = 20, orderBy: string = 'created_at', order: string = 'desc', search: string = '', company: number = 1): void {
        this.loadingSubject.next(true);
        this._recommenderService.listRecommendations(company, page, limit, orderBy, order, search).then(response => {
            this.recommenderSubject.next(response.data);
            this.loadingSubject.next(false);
        });
    }
}
